// ── BCH Live — Stream Cards ───────────────────────────────────────
//
// Card variants share one thumbnail/meta layout so the directory reads
// consistently:
//   • StreamCard      — the standard grid card
//   • FeaturedStream  — large hero treatment
//   • StreamCardSkeleton / FeaturedSkeleton — loading states
//
// Every card surfaces: thumbnail, live badge, viewers, avatar, duration,
// category, topic tags and a BCH-tipping indicator.

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Eye, User, Radio, Clock, Coins, Calendar, Play } from 'lucide-react';
import { useNostrProfile } from '../../hooks/useNostrQuery';
import { extractUsername } from '../../lib/streamCredentials';
import VerifiedBadge from '../VerifiedBadge';
import { useNip05Verified } from '../../hooks/useNip05Verified';
import {
  formatDuration, formatViewers, formatScheduledTime, countdownTo,
  trendingReason, type LiveStream, type StreamSignals,
} from '../../lib/liveStream';

/**
 * Route for a stream. Prefers the vanity `/live/<username>` URL, but ONLY
 * when the NIP-05 is verified to resolve to this creator.
 *
 * A `nip05` in a kind 0 profile is an unverified claim: anyone can put
 * `alice@bchnostr.com` in their profile. Routing on the raw claim meant two
 * creators could produce the same `/live/alice` link, and clicking either
 * loaded whoever the directory actually maps `alice` to — i.e. you'd click
 * one channel and get a different one. The pubkey URL is always unambiguous,
 * so fall back to it unless the claim is confirmed.
 */
export function streamHref(
  stream: LiveStream,
  nip05?: string,
  verified?: boolean,
): string {
  const username = verified ? extractUsername(nip05) : null;
  return username ? `/live/${username}` : `/live/pk/${stream.hostPubkey}`;
}

// ── Live badge with pulsing dot ───────────────────────────────────
export function LiveBadge({ size = 'sm' }: { size?: 'sm' | 'lg' }) {
  const lg = size === 'lg';
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md bg-red-600 font-extrabold uppercase tracking-wide text-white shadow-sm ${
        lg ? 'px-2.5 py-1 text-[11px]' : 'px-2 py-0.5 text-[10px]'
      }`}
    >
      <span className="relative flex h-1.5 w-1.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-white" />
      </span>
      Live
    </span>
  );
}

// ── Elapsed runtime, ticking every second ─────────────────────────
function DurationBadge({ startsAt }: { startsAt: number }) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!startsAt) return;
    const i = setInterval(() => force(n => n + 1), 1000);
    return () => clearInterval(i);
  }, [startsAt]);
  if (!startsAt) return null;
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-black/75 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-white backdrop-blur-sm">
      <Clock className="h-2.5 w-2.5" />
      {formatDuration(startsAt)}
    </span>
  );
}

// ── Countdown for scheduled streams ───────────────────────────────
export function CountdownBadge({ startsAt, className = '' }: { startsAt: number; className?: string }) {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const i = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(i);
  }, []);
  const cd = useMemo(() => countdownTo(startsAt, now), [startsAt, now]);
  if (!startsAt) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md bg-primary/15 px-2 py-0.5 text-[11px] font-bold tabular-nums text-primary ${className}`}
    >
      <Calendar className="h-3 w-3" />
      {cd.label}
    </span>
  );
}

// ── Thumbnail with gradient fallback ──────────────────────────────
function Thumb({
  stream, tall, children,
}: { stream: LiveStream; tall?: boolean; children?: React.ReactNode }) {
  const [failed, setFailed] = useState(false);
  const showImage = stream.image && !failed;

  return (
    <div className={`relative w-full overflow-hidden bg-muted ${tall ? 'aspect-video' : 'aspect-video'}`}>
      {showImage ? (
        <img
          src={stream.image}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        />
      ) : (
        <div
          className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${
            stream.category?.gradient || 'from-emerald-500/25 to-teal-700/25'
          }`}
        >
          <Radio className="h-8 w-8 text-white/40" />
        </div>
      )}

      {/* Bottom scrim so overlaid text stays readable on any thumbnail */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/70 to-transparent" />

      {children}
    </div>
  );
}

// ── Tipping-enabled indicator ─────────────────────────────────────
function TipIndicator({ compact }: { compact?: boolean }) {
  return (
    <span
      title="BCH tipping enabled"
      className={`inline-flex items-center gap-1 rounded-md bg-emerald-500/15 font-bold text-emerald-500 ring-1 ring-inset ring-emerald-500/30 ${
        compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-[11px]'
      }`}
    >
      <Coins className={compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
      BCH
    </span>
  );
}

// ── Category chip ─────────────────────────────────────────────────
export function CategoryChip({ label, to }: { label: string; to?: string }) {
  const cls =
    'inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground';
  if (!to) return <span className={cls}>{label}</span>;
  return (
    <Link to={to} onClick={e => e.stopPropagation()} className={`${cls} hover:bg-accent hover:text-foreground`}>
      {label}
    </Link>
  );
}

// ══════════════════════════════════════════════════════════════════
//  STANDARD CARD
// ══════════════════════════════════════════════════════════════════

export const StreamCard = React.memo(function StreamCard({
  stream, signals,
}: { stream: LiveStream; signals?: StreamSignals }) {
  const { profile } = useNostrProfile(stream.hostPubkey);
  const nip05Verified = useNip05Verified(profile?.nip05, stream.hostPubkey);
  const href = streamHref(stream, profile?.nip05, nip05Verified);
  const name = profile?.display_name || profile?.name || stream.hostPubkey.slice(0, 8);
  const reason = signals ? trendingReason(signals) : null;

  return (
    <Link
      to={href}
      className="group block overflow-hidden rounded-xl border border-border bg-card transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Thumb stream={stream}>
        <div className="absolute left-2 top-2 flex flex-wrap items-center gap-1.5">
          {stream.isLive && <LiveBadge />}
          {stream.isUpcoming && <CountdownBadge startsAt={stream.startsAt} />}
          {stream.hasEnded && (
            <span className="inline-flex items-center gap-1 rounded-md bg-black/70 px-2 py-0.5 text-[10px] font-semibold text-white/90 backdrop-blur-sm">
              <Play className="h-2.5 w-2.5" /> Ended
            </span>
          )}
        </div>

        <div className="absolute right-2 top-2 flex flex-col items-end gap-1.5">
          {stream.isLive && stream.viewerCount > 0 && (
            <span className="inline-flex items-center gap-1 rounded-md bg-black/75 px-1.5 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm">
              <Eye className="h-2.5 w-2.5" /> {formatViewers(stream.viewerCount)}
            </span>
          )}
          {stream.isLive && <DurationBadge startsAt={stream.startsAt} />}
        </div>

        {/* Category sits bottom-left over the scrim */}
        {stream.category && (
          <div className="absolute bottom-2 left-2">
            <span className="rounded-md bg-white/15 px-2 py-0.5 text-[10px] font-bold text-white backdrop-blur-sm">
              {stream.category.label}
            </span>
          </div>
        )}
        {stream.tippingEnabled && (
          <div className="absolute bottom-2 right-2">
            <TipIndicator compact />
          </div>
        )}
      </Thumb>

      <div className="p-3">
        <div className="flex items-start gap-2.5">
          {profile?.picture ? (
            <img
              src={profile.picture}
              alt=""
              loading="lazy"
              className="h-9 w-9 shrink-0 rounded-full object-cover ring-2 ring-border"
            />
          ) : (
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 ring-2 ring-border">
              <User className="h-4 w-4 text-primary/60" />
            </div>
          )}

          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-bold leading-snug transition-colors group-hover:text-primary">
              {stream.title}
            </h3>
            <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
              <span className="truncate">{name}</span>
              <VerifiedBadge nip05={profile?.nip05} pubkey={stream.hostPubkey} size="xs" />
            </p>

            {stream.isUpcoming && stream.startsAt > 0 && (
              <p className="mt-1 truncate text-[11px] font-medium text-primary/80">
                {formatScheduledTime(stream.startsAt)}
              </p>
            )}

            {reason && (
              <p className="mt-1 truncate text-[11px] font-semibold text-emerald-500">{reason}</p>
            )}

            {stream.topics.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {stream.topics.slice(0, 3).map(t => (
                  <span
                    key={t}
                    className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
});

// ══════════════════════════════════════════════════════════════════
//  FEATURED HERO
// ══════════════════════════════════════════════════════════════════

export const FeaturedStream = React.memo(function FeaturedStream({
  stream, signals,
}: { stream: LiveStream; signals?: StreamSignals }) {
  const { profile } = useNostrProfile(stream.hostPubkey);
  const nip05Verified = useNip05Verified(profile?.nip05, stream.hostPubkey);
  const href = streamHref(stream, profile?.nip05, nip05Verified);
  const name = profile?.display_name || profile?.name || stream.hostPubkey.slice(0, 8);
  const reason = signals ? trendingReason(signals) : null;

  return (
    <Link
      to={href}
      className="group relative block overflow-hidden rounded-2xl border border-border bg-card transition-all duration-200 hover:border-primary/50 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="grid gap-0 md:grid-cols-[1.6fr_1fr]">
        <Thumb stream={stream} tall>
          <div className="absolute left-3 top-3 flex flex-wrap items-center gap-2">
            {stream.isLive ? <LiveBadge size="lg" /> : <CountdownBadge startsAt={stream.startsAt} />}
            {stream.isLive && stream.viewerCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-md bg-black/75 px-2 py-1 text-[11px] font-semibold text-white backdrop-blur-sm">
                <Eye className="h-3 w-3" /> {formatViewers(stream.viewerCount)}
              </span>
            )}
            {stream.isLive && <DurationBadge startsAt={stream.startsAt} />}
          </div>
        </Thumb>

        <div className="flex flex-col justify-center gap-3 p-4 sm:p-5">
          <div className="flex items-center gap-2">
            <span className="rounded-md bg-primary/15 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-primary">
              Featured
            </span>
            {stream.category && <CategoryChip label={stream.category.label} />}
          </div>

          <h2 className="line-clamp-2 text-lg font-extrabold leading-tight sm:text-xl">
            {stream.title}
          </h2>

          {stream.summary && (
            <p className="line-clamp-2 text-sm text-muted-foreground">{stream.summary}</p>
          )}

          <div className="flex items-center gap-2.5">
            {profile?.picture ? (
              <img src={profile.picture} alt="" className="h-10 w-10 rounded-full object-cover ring-2 ring-border" />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 ring-2 ring-border">
                <User className="h-5 w-5 text-primary/60" />
              </div>
            )}
            <div className="min-w-0">
              <p className="flex items-center gap-1 text-sm font-bold">
                <span className="truncate">{name}</span>
                <VerifiedBadge nip05={profile?.nip05} pubkey={stream.hostPubkey} size="xs" />
              </p>
              {stream.isUpcoming && stream.startsAt > 0 ? (
                <p className="truncate text-[11px] text-primary/80">{formatScheduledTime(stream.startsAt)}</p>
              ) : reason ? (
                <p className="truncate text-[11px] font-semibold text-emerald-500">{reason}</p>
              ) : (
                profile?.nip05 && <p className="truncate text-[11px] text-muted-foreground">{profile.nip05}</p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {stream.tippingEnabled && <TipIndicator />}
            {stream.topics.slice(0, 3).map(t => (
              <span key={t} className="rounded-md bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                #{t}
              </span>
            ))}
          </div>

          <span className="mt-1 inline-flex w-fit items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-bold text-primary-foreground transition-transform group-hover:scale-[1.02]">
            {stream.isLive ? 'Watch now' : 'View stream'}
          </span>
        </div>
      </div>
    </Link>
  );
});

// ══════════════════════════════════════════════════════════════════
//  SKELETONS
// ══════════════════════════════════════════════════════════════════

export function StreamCardSkeleton() {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="live-skeleton aspect-video w-full" />
      <div className="flex items-start gap-2.5 p-3">
        <div className="live-skeleton h-9 w-9 shrink-0 rounded-full" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="live-skeleton h-3.5 w-4/5 rounded" />
          <div className="live-skeleton h-3 w-1/2 rounded" />
          <div className="live-skeleton h-2.5 w-2/3 rounded" />
        </div>
      </div>
    </div>
  );
}

export function FeaturedSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <div className="grid gap-0 md:grid-cols-[1.6fr_1fr]">
        <div className="live-skeleton aspect-video w-full" />
        <div className="space-y-3 p-5">
          <div className="live-skeleton h-4 w-24 rounded" />
          <div className="live-skeleton h-6 w-4/5 rounded" />
          <div className="live-skeleton h-3 w-full rounded" />
          <div className="flex items-center gap-2.5 pt-1">
            <div className="live-skeleton h-10 w-10 rounded-full" />
            <div className="space-y-1.5">
              <div className="live-skeleton h-3 w-28 rounded" />
              <div className="live-skeleton h-2.5 w-20 rounded" />
            </div>
          </div>
          <div className="live-skeleton h-9 w-32 rounded-xl" />
        </div>
      </div>
    </div>
  );
}

export function StreamGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <StreamCardSkeleton key={i} />
      ))}
    </div>
  );
}
